import { Net, NetCode, NetProfileAuthConnectResult, NetStatus } from '@/graphql/generated/types.generated';
import { updateProfile, type ConnectSurface } from '@/utils/profiles/update-profile';
import { LoadingButton } from '@mui/lab';
import { Card, CardActionArea, Typography, Button, Chip, Avatar, Stack, Divider } from '@mui/material';
import { Skeleton } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import posthog from 'posthog-js';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';
import { NetProfileAuthConnectResultDocument } from '@/graphql/generated/net-profile-auth-connect-result.generated';
import InstagramRouteDialog from './InstagramRouteDialog';
import { netDisplayName } from '@/utils/profiles/visible-nets';

export type NetCardState = 'default' | 'soon' | 'beta' | 'unavailable';

/**
 * 'card' is the tall tile: icon above name above button. Right for the wide multi-column pickers.
 * 'row' lays the same content on one line, about a third of the height, so a long network list
 * fits a narrow dialog without an inner scrollbar. Use it wherever width is tight.
 */
export type NetCardLayout = 'card' | 'row';

type NetCardProps = {
    net: Net;
    /** Called only when the attempt actually added or reconnected a profile, not on every popup close. */
    onConnect?: () => void;
    isConnected?: boolean;
    brandId?: number;
    /** Where this card is rendered, e.g. 'onboarding'. Carried onto the analytics events. */
    surface?: ConnectSurface;
    layout?: NetCardLayout;
};

export const NetCard = ({
    net,
    onConnect,
    isConnected = false,
    brandId,
    surface,
    layout = 'card',
}: NetCardProps) => {
    const { t } = useTranslation();
    const [getConnectResult] = useLazyQuery(NetProfileAuthConnectResultDocument, {
        fetchPolicy: 'network-only',
    });
    const [loading, setLoading] = useState(false);
    const [routeOpen, setRouteOpen] = useState(false);

    const { name, code, status } = net;
    const isRow = layout === 'row';

    // The single Instagram card stands for both routes into the network. Clicking it asks which
    // login the person has instead of sending them straight into one of the two OAuth flows.
    // See visible-nets.ts for why the second Instagram card is not rendered at all.
    const isInstagram = code === NetCode.InstagramV2;

    // This card stands for the whole network rather than one of its two routes, so it is labelled
    // "Instagram" regardless of what the API calls INSTAGRAM_V2. Shared with the brands page, which
    // shows both routes and needs them named by the login each one opens.
    const displayName = netDisplayName(code, name);

    /**
     * The popup closes itself the same way on success, error and cancel, so closing it proves
     * nothing. The server reports whether a profile was added or reconnected since it opened, and
     * only that counts as a connection.
     */
    const handlePopupClosed = async (targetCode: string, openedAt: number) => {
        const { data } = await getConnectResult({
            variables: { openedSecondsAgo: Math.ceil((Date.now() - openedAt) / 1000) },
        });
        // No data means the check itself failed and the outcome is unknown, so behave as before
        // rather than hide a connection that may well have worked.
        const connected = !data || data.netProfileAuthConnectResult !== NetProfileAuthConnectResult.None;

        if (connected) {
            await onConnect?.();
        } else if (POSTHOG_ENABLED) {
            posthog.capture('profile.connect.failed', {
                netCode: targetCode.toLowerCase(),
                update: false,
                surface,
                secondsOpen: Math.round((Date.now() - openedAt) / 1000),
            });
        }
        setLoading(false);
    };

    const startConnect = (targetCode: string) => {
        setLoading(true);
        const openedAt = Date.now();
        const opened = updateProfile(
            targetCode.toLowerCase(),
            () => {
                handlePopupClosed(targetCode, openedAt);
            },
            false,
            brandId,
            surface,
        );
        // A blocked popup never closes, so nothing would ever clear the spinner.
        if (!opened) setLoading(false);
    };

    const handleConnect = () => {
        if (isInstagram) {
            setRouteOpen(true);
            return;
        }
        startConnect(code);
    };

    const icon = (
        <Avatar
            variant="square"
            alt={displayName}
            src={`/social/${code.toLowerCase()}.svg`}
            sx={{ width: isRow ? 28 : 40, height: isRow ? 28 : 40 }}
        />
    );

    const title = (
        <Stack alignItems="center" justifyContent="center" direction="row" gap={1}>
            <Typography
                variant={isRow ? 'subtitle2' : 'h6'}
                fontWeight={600}
                color={status === NetStatus.Discontinued ? 'text.disabled' : 'text.primary'}
            >
                {displayName}
            </Typography>
            {status === NetStatus.ComingSoon && (
                <Chip
                    color="default"
                    label="Soon"
                    size="small"
                    sx={{ '.MuiChip-label': { fontWeight: 500 }, mr: 1 }}
                />
            )}
        </Stack>
    );

    // Rendered as a span, not a button: the whole card is the click target, and a nested button
    // would be invalid markup and a second tab stop for the same action.
    const action =
        status === NetStatus.Active ? (
            <LoadingButton
                component="span"
                variant="outlined"
                color={isConnected ? 'success' : 'primary'}
                fullWidth={!isRow}
                size={isRow ? 'small' : 'medium'}
                loading={loading}
                tabIndex={-1}
                sx={{ pointerEvents: 'none' }}
            >
                {isConnected ? t('str.status.connected') : t('net.connect')}
            </LoadingButton>
        ) : status === NetStatus.ComingSoon ? (
            <Button fullWidth={!isRow} size={isRow ? 'small' : 'medium'} onClick={() => alert('Notified')}>
                {t('net.notify')}
            </Button>
        ) : (
            <Typography variant="body2" color="text.disabled">
                {t('net.unavailable')}
            </Typography>
        );

    const body = isRow ? (
        <Stack direction="row" alignItems="center" gap={1.5} px={2} py={1.25}>
            {icon}
            <Stack flexGrow={1} alignItems="flex-start">
                {title}
            </Stack>
            {action}
        </Stack>
    ) : (
        <Stack gap={2.5} py={2}>
            <Stack px={2} gap={1}>
                <Stack alignItems="center">{icon}</Stack>
                {title}
            </Stack>
            <Divider />
            <Stack px={2} alignItems="center">
                {action}
            </Stack>
        </Stack>
    );

    const isActionable = status === NetStatus.Active;

    return (
        <Card sx={{ width: 1, maxWidth: isRow ? 1 : 300 }} elevation={0}>
            {isActionable ? (
                <CardActionArea
                    onClick={handleConnect}
                    disabled={loading}
                    data-id={`net-connect-${code.toLowerCase()}`}
                >
                    {body}
                </CardActionArea>
            ) : (
                body
            )}
            {isInstagram && (
                <InstagramRouteDialog
                    open={routeOpen}
                    surface={surface}
                    onClose={() => setRouteOpen(false)}
                    onSelect={(targetCode) => startConnect(targetCode)}
                />
            )}
        </Card>
    );
};

export const NetCardLoading = ({ layout = 'card' }: { layout?: NetCardLayout }) =>
    layout === 'row' ? (
        <Card sx={{ width: 1 }} elevation={0}>
            <Stack direction="row" alignItems="center" gap={1.5} px={2} py={1.25}>
                <Skeleton variant="rectangular" width={28} height={28} sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" width={90} height={24} sx={{ flexGrow: 1 }} />
                <Skeleton variant="rectangular" width={80} height={26} />
            </Stack>
        </Card>
    ) : (
        <Card sx={{ width: 1, maxWidth: 300 }} elevation={0}>
            <Stack gap={2.5} py={2}>
                <Stack px={2} gap={1}>
                    <Stack alignItems="center">
                        <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
                    </Stack>
                    <Stack alignItems="center">
                        <Skeleton variant="text" width={90} height={24} />
                    </Stack>
                </Stack>
                <Divider />
                <Stack px={2} alignItems="center">
                    <Skeleton variant="rectangular" width="100%" height={28} />
                </Stack>
            </Stack>
        </Card>
    );
